import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type {
  CreatePrinterRequest,
  PrinterAdminRow,
  PrinterEngine,
  PrinterTransport,
  UpdatePrinterRequest,
} from '@techgitter/contracts';
import {
  ENGINE_OPTIONS,
  TRANSPORT_OPTIONS,
  resolveTransport,
} from '../../services/printerHelpers';

interface PrinterEditDialogProps {
  open: boolean;
  /** The row being edited, or null when creating. */
  printer: PrinterAdminRow | null;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (
    payload: CreatePrinterRequest | UpdatePrinterRequest,
    id: string | null,
  ) => void;
}

const ACCENT = '#4A90D9';

export const PrinterEditDialog: React.FC<PrinterEditDialogProps> = ({
  open,
  printer,
  isSaving,
  error,
  onClose,
  onSave,
}) => {
  const isCreate = printer === null;

  const [siteId, setSiteId] = useState('');
  const [role, setRole] = useState('');
  const [engine, setEngine] = useState<PrinterEngine | ''>('');
  // '' = auto (derive from engine); otherwise an explicit override.
  const [transport, setTransport] = useState<PrinterTransport | ''>('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [printerName, setPrinterName] = useState('');
  const [interJobDelayMs, setInterJobDelayMs] = useState('0');
  const [isActive, setIsActive] = useState(true);

  // Reset/seed the form whenever the dialog (re)opens.
  useEffect(() => {
    if (!open) return;
    setSiteId(printer?.siteId ?? '');
    setRole(printer?.role ?? '');
    setEngine(printer?.engine ?? '');
    setTransport(printer?.transport ?? '');
    setHost(printer?.host ?? '');
    setPort(printer?.port != null ? String(printer.port) : '');
    setPrinterName(printer?.printerName ?? '');
    setInterJobDelayMs(
      printer?.interJobDelayMs != null ? String(printer.interJobDelayMs) : '0',
    );
    setIsActive(printer?.isActive ?? true);
  }, [open, printer]);

  // Effective transport drives which of host / printerName is required.
  const resolved = useMemo(
    () => resolveTransport(engine || null, transport || null),
    [engine, transport],
  );
  const needsHost = resolved === 'tcp9100';
  const needsPrinterName = resolved === 'spooler';

  const isValid =
    siteId.trim().length > 0 &&
    role.trim().length > 0 &&
    engine !== '' &&
    (!needsHost || host.trim().length > 0) &&
    (!needsPrinterName || printerName.trim().length > 0);

  const handleSave = () => {
    const trimmedPort = port.trim();
    const trimmedDelay = interJobDelayMs.trim();
    const payload: CreatePrinterRequest = {
      siteId: siteId.trim(),
      role: role.trim(),
      engine: engine as PrinterEngine,
      transport: transport === '' ? null : (transport as PrinterTransport),
      host: host.trim() === '' ? null : host.trim(),
      port: trimmedPort === '' ? null : Number(trimmedPort),
      printerName: printerName.trim() === '' ? null : printerName.trim(),
      interJobDelayMs: trimmedDelay === '' ? 0 : Number(trimmedDelay),
      isActive,
    };
    onSave(payload, printer?.id ?? null);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isCreate ? 'Add printer' : 'Edit printer'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Site ID"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              required
              size="small"
              fullWidth
              placeholder="e.g. TS-HFO-01"
              helperText="Service Bus session prefix / agent site id"
            />
            <TextField
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              required
              size="small"
              fullWidth
              placeholder="e.g. label-counter"
              helperText="Unique per site"
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Engine"
              value={engine}
              onChange={(e) => setEngine(e.target.value as PrinterEngine)}
              required
              size="small"
              fullWidth
            >
              {ENGINE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Transport"
              value={transport}
              onChange={(e) =>
                setTransport(e.target.value as PrinterTransport | '')
              }
              size="small"
              fullWidth
              helperText={
                transport === '' && resolved
                  ? `Auto → ${resolved}`
                  : 'Override only if needed'
              }
            >
              <MenuItem value="">
                <em>Auto (from engine)</em>
              </MenuItem>
              {TRANSPORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Host"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              required={needsHost}
              disabled={needsPrinterName}
              size="small"
              fullWidth
              placeholder="192.168.50.242"
              helperText={needsHost ? 'Required for tcp9100' : 'tcp9100 only'}
            />
            <TextField
              label="Port"
              value={port}
              onChange={(e) => setPort(e.target.value)}
              disabled={needsPrinterName}
              size="small"
              type="number"
              sx={{ width: 140 }}
              placeholder="9100"
              helperText="Default 9100"
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Printer name"
              value={printerName}
              onChange={(e) => setPrinterName(e.target.value)}
              required={needsPrinterName}
              disabled={needsHost}
              size="small"
              fullWidth
              placeholder="HP_M478f"
              helperText={
                needsPrinterName
                  ? 'Required for spooler (CUPS / Windows queue)'
                  : 'spooler only'
              }
            />
            <TextField
              label="Inter-job delay (ms)"
              value={interJobDelayMs}
              onChange={(e) => setInterJobDelayMs(e.target.value)}
              size="small"
              type="number"
              sx={{ width: 200 }}
              helperText="200–500 for USB bridges"
            />
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="Active"
          />
          <Typography variant="caption" sx={{ color: '#888' }}>
            Inactive printers are hidden from the agent. Deleting a printer
            frees its (site, role) pair.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || isSaving}
          sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#3B82C4' } }}
        >
          {isSaving ? 'Saving…' : isCreate ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
